import {
  Alert,
  Container,
  Divider,
  Grid,
  Group,
  Space,
  Spoiler,
  Text,
  Title,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import { OpenComponent } from '../../enums/misc.enum';
import { formatDateWithTime } from '../../helpers/date-convert';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { ErrorResponse } from '../../type/error-response.types';
import { RetrieveAccountResponse } from '../../type/retrieve-account.types';
import BackButton from '../buttons/back-button.component';
import XCol3 from '../x-components/grid-col/x-col-3.component';
import XSafeTextInfo from '../x-components/x-safe-text-info.component';
import XTextStackInfo from '../x-components/x-text-stack-info.component';
import SkeletonAdminUserInformation from './skeleton-loading/skeleton-admin-user-information.component';

function AdminUserInformation(props: Readonly<Props>) {
  const { selectedId, setComponentManager } = props;
  const axiosPrivate = useAxiosPrivate();
  const [user, setUser] = useState<RetrieveAccountResponse>();
  const [loading, setLoading] = useState(false);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get(`/web/accounts/${selectedId}`);
      const retrievedUser: RetrieveAccountResponse = response.data;
      setUser(retrievedUser);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-user-data',
        message:
          error?.error ||
          err.response?.data?.error ||
          'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <Container fluid>
      <BackButton
        handleClick={() => {
          setComponentManager(OpenComponent.HOME);
        }}
      />
      <Title order={2} my="lg">
        Account Information
      </Title>

      <Divider my="xl" />
      {loading ? (
        <SkeletonAdminUserInformation />
      ) : user ? (
        <>
          <Text size="xl" weight={700}>
            User Information
          </Text>
          <Space h="xl" />
          {user?.disabledReason && !user?.isActive && (
            <Alert title="Disabled Reason" color="red" mb={22}>
              <Spoiler
                maxHeight={110}
                showLabel={<Text size="sm">Show more</Text>}
                hideLabel={<Text size="sm">Hide</Text>}
              >
                {user?.disabledReason}
              </Spoiler>
            </Alert>
          )}
          <Grid>
            <XCol3>
              <XTextStackInfo
                label={labels.firstName}
                value={user.user.firstName}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.middleName}
                value={user.user.middleName}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.lastName}
                value={user.user.lastName}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.emailAddress}
                value={user.contact.email}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.username}
                value={user.user.username}
              />
            </XCol3>
          </Grid>

          <Divider my="xl" />
          <Text size="xl" weight={700}>
            Address
          </Text>
          <Space h="xl" />
          <Grid>
            <XCol3>
              <XTextStackInfo
                label={labels.city}
                value={user.user.address?.city}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.bldgNameNo}
                value={user.user.address?.buildingNameAndNumber}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.barangay}
                value={user.user.address?.barangay}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.streetAddress}
                value={user.user.address?.line1}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.postalCode}
                value={user.user.address?.postalCode}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.province}
                value={user.user.address?.state}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.country}
                value={user.user.address?.countryId === 1 ? 'Philippines' : ''}
              />
            </XCol3>
          </Grid>
          <Divider my="xl" />
          <Text size="xl" weight={700}>
            Contact Information
          </Text>
          <Space h="xl" />
          <Grid>
            <XCol3>
              <XTextStackInfo
                label={labels.contactNumber}
                value={user.contact.phone.primary}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.mobileNumber}
                value={user.contact.phone.mobile}
              />
            </XCol3>
            <XCol3>
              <XTextStackInfo
                label={labels.fax}
                value={user.user.address?.fax}
              />
            </XCol3>
          </Grid>
          <Space />
          <Divider my="xl" />

          <Space h="xl" />

          <Grid>
            <Grid.Col>
              <Group>
                <Text size="sm" weight={600}>
                  Date created:
                </Text>
                <XSafeTextInfo
                  size="sm"
                  data={formatDateWithTime(
                    user.dateCreated ? user.dateCreated?.toString() : ''
                  )}
                />
              </Group>
            </Grid.Col>
            <Grid.Col span={12}>
              <Group>
                <Text size="sm" weight={600}>
                  Date last logged in:
                </Text>
                <XSafeTextInfo
                  size="sm"
                  data={formatDateWithTime(
                    user.dateLastLoggedIn
                      ? user.dateLastLoggedIn?.toString()
                      : ''
                  )}
                />
              </Group>
            </Grid.Col>
            <Grid.Col span={12}>
              <Group>
                <Text size="sm" weight={600}>
                  Password expiration date:
                </Text>
                <XSafeTextInfo
                  size="sm"
                  data={formatDateWithTime(
                    user.datePasswordExpiration
                      ? user.datePasswordExpiration?.toString()
                      : ''
                  )}
                />
              </Group>
            </Grid.Col>
          </Grid>
        </>
      ) : (
        'No account data to show'
      )}
    </Container>
  );
}

export default AdminUserInformation;

interface Props {
  selectedId: number;
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
}

const labels = {
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  streetAddress: 'Street address',
  bldgNameNo: 'Building name and number',
  city: 'City',
  province: 'Province',
  barangay: 'Barangay',
  postalCode: 'Postal code',
  country: 'Country',
  contactNumber: 'Primary contact number',
  fax: 'FAX',
  mobileNumber: 'Mobile phone number',
  emailAddress: 'Email address',
  username: 'Username',
};
